export const navbarWidths = {
  expanded: 220,
  collapsed: 82
};

type TAppMenuItem = false | { title: string; link: string; target: string };

interface TNavSubmenuItem {
  title: string;
  url: string;
  active: RegExp;
  id?: string;
  permissions?: string[];
}

interface TNavSection {
  title: string;
  icon: string;
  id?: string;
  url?: string;
  active?: RegExp;
  submenu: TNavSubmenuItem[];
}

export const appMenuItems = (
  isRecruit: boolean,
  isOnboard: boolean,
  isEngage: boolean,
  newOnboardEnabled: boolean,
  hasContractTemplateAccess: boolean,
  hasAgenticAccess: boolean
): TAppMenuItem[] => [
  !isRecruit && {
    title: ':Recruit',
    link: '/admin/jobs',
    target: '_self'
  },
  !isOnboard && {
    title: ':Onboard',
    link: 'https://scouttalentonboard.com/',
    target: '_blank'
  },
  !isOnboard &&
    newOnboardEnabled &&
    hasContractTemplateAccess && {
      title: ':Onboard (new)',
      link: '/admin/onboard/offers',
      target: '_self'
    },
  !isEngage && {
    title: ':Engage',
    link: hasAgenticAccess ? '/admin/candidate_sourcing' : '/admin/engage?engage-search',
    target: '_self'
  },
  {
    title: 'Scout Learning',
    link: 'https://scouttalenthq.com/talent-acquisition-software/scout-learning',
    target: '_blank'
  },
  {
    title: 'Marketplace',
    link: 'https://scouttalenthq.com/marketplace',
    target: '_blank'
  }
];

interface DefaultMenuItemsOptions {
  newEngage: boolean;
  isEngage: boolean;
  hasQuestionLibraryAccess: boolean;
  hasTagManagementAccess: boolean;
  hasJobTemplateAccess: boolean;
  hasAgenticAccess: boolean;
  isOnboard?: boolean;
  newOnboardEnabled?: boolean;
  leadershipMetricsDashboardEnabled?: boolean;
}

export const defaultMenuItems = ({
  newEngage,
  isEngage,
  hasQuestionLibraryAccess,
  hasTagManagementAccess,
  hasJobTemplateAccess,
  hasAgenticAccess,
  isOnboard = false,
  leadershipMetricsDashboardEnabled = false,
  newOnboardEnabled = false
}: DefaultMenuItemsOptions): TNavSection[] => {
  if (isOnboard && newOnboardEnabled) {
    return [
      {
        title: 'Offers',
        icon: 'offers',
        id: 'offers-left-tab',
        url: '/admin/onboard/offers',
        active: /\/admin\/onboard\/offers/,
        submenu: []
      },
      {
        title: 'Candidates',
        icon: 'candidates',
        id: 'candidates-left-tab',
        url: '/admin/onboard/candidates',
        active: /\/admin\/onboard\/candidates/,
        submenu: []
      },
      {
        title: 'Resources',
        icon: 'resources',
        id: 'resources-left-tab',
        submenu: [
          {
            title: 'Contracts',
            url: '/admin/onboard/resources/contracts',
            active: /\/admin\/onboard\/resources\/contracts/,
            id: 'contracts-left-tab'
          },
          {
            title: 'Documents',
            url: '/admin/onboard/resources/documents',
            active: /\/admin\/onboard\/resources\/documents/,
            id: 'documents-left-tab'
          },
          {
            title: 'Smart Forms',
            url: '/admin/onboard/resources/smart-forms',
            active: /\/admin\/onboard\/resources\/smart-forms/,
            id: 'smart-forms-left-tab'
          },
          {
            title: 'Email Templates',
            url: '/admin/onboard/resources/email-templates',
            active: /\/admin\/onboard\/resources\/email-templates/,
            id: 'email-templates-left-tab'
          },
          {
            title: 'Offer Templates',
            url: '/admin/onboard/resources/offer-templates',
            active: /\/admin\/onboard\/resources\/offer-templates/,
            id: 'offer-templates-left-tab'
          },
          {
            title: 'Placeholders',
            url: '/admin/onboard/resources/placeholders',
            active: /\/admin\/onboard\/resources\/placeholders/,
            id: 'placeholders-left-tab'
          },
          {
            title: 'Categories',
            url: '/admin/onboard/resources/categories',
            active: /\/admin\/onboard\/resources\/categories/,
            id: 'categories-left-tab'
          },
          {
            title: 'Departments',
            url: '/admin/onboard/resources/departments',
            active: /\/admin\/onboard\/resources\/departments/,
            id: 'departments-left-tab'
          },
          {
            title: 'Approvers',
            url: '/admin/onboard/resources/approvers',
            active: /\/admin\/onboard\/resources\/approvers/,
            id: 'approvers-left-tab'
          }
        ]
      },
      {
        title: 'Settings',
        icon: 'settings',
        id: 'settings-left-tab',
        url: '/admin/onboard/settings',
        active: /\/admin\/onboard\/settings/,
        submenu: []
      },
      {
        title: 'Trash',
        icon: 'trash',
        id: 'trash-left-tab',
        url: '/admin/onboard/trash',
        active: /\/admin\/onboard\/trash/,
        submenu: []
      }
    ];
  }

  if (isEngage) {
    const legacyEngageSection: TNavSection = {
      title: 'Legacy :Engage',
      icon: 'spanner',
      id: 'legacy-engage-left-tab',
      submenu: [
        {
          title: 'AI Search',
          url: '/admin/engage?engage-search',
          active: /\/admin\/engage\?engage-search/,
          id: 'ai-search-left-tab'
        },
        {
          title: 'Candidate Pools',
          url: '/admin/engage?candidate-pools',
          active: /\/admin\/engage\?candidate-pools/,
          id: 'legacy-candidate-pools-left-tab'
        }
      ]
    };

    const agenticEngageSection: TNavSection = {
      title: 'Agentic :Engage',
      icon: 'people_search',
      id: 'agentic-engage-left-tab',
      submenu: [
        {
          title: 'New Search',
          url: '/admin/candidate_sourcing/searches/new_search',
          active: /\/admin\/candidate_sourcing\/searches\/new_search/,
          id: 'agentic-new-search-left-tab'
        },
        {
          title: 'Searches',
          url: '/admin/candidate_sourcing',
          active: /\/admin\/candidate_sourcing$/,
          id: 'agentic-searches-left-tab'
        },
        {
          title: 'Candidate Pools',
          url: '/admin/candidate_sourcing?candidate-pools',
          active: /\/admin\/candidate_sourcing\?candidate-pools/,
          id: 'agentic-candidate-pools-left-tab'
        },
        {
          title: 'Sequences',
          url: '/admin/candidate_sourcing?sequences',
          active: /\/admin\/candidate_sourcing\?sequences/,
          id: 'agentic-sequences-left-tab'
        }
      ]
    };

    const engageSections: TNavSection[] = [];
    if (newEngage && hasAgenticAccess) {
      engageSections.push(agenticEngageSection, legacyEngageSection);
    } else if (newEngage) {
      engageSections.push(legacyEngageSection);
    }

    return [
      {
        title: 'Home',
        icon: 'home',
        id: 'engage-home-left-tab',
        submenu: [
          {
            title: 'Search',
            id: 'engage-home-left-search',
            url: '/admin/search/advanced',
            active: /\/admin\/search\/advanced/,
            // Admin::SearchesController gates #advanced on `advanced_search`. Without this
            // the item rendered for everyone and bounced unpermitted users to /admin with
            // "Permission denied" (UNS-7479). Mirrors 'Saved Searches' below.
            permissions: ['Advanced Search']
          }
        ]
      },
      {
        title: 'Tools',
        icon: 'spanner',
        id: 'engage-spanner-left-tab',
        submenu: [
          {
            title: 'Saved Searches',
            url: '/admin/search/advanced/saved',
            active: /\/admin\/search\/advanced\/saved/,
            permissions: ['Advanced Search'],
            id: 'engage-saved-searches-left-tab'
          }
        ]
      },
      ...engageSections
    ];
  }
  return [
    {
      title: 'Recruitment',
      id: 'recruitment-tab-sidebar',
      icon: 'people_search',
      submenu: [
        {
          title: 'Approval Forms',
          url: '/admin/requisitions',
          active: /\/admin\/requisitions/,
          permissions: [
            'Create / Edit Approval Forms (HR use)',
            'Edit Previously Submitted Form',
            "See Forms I'm Assigned To or Approving",
            'Submit Approval Form'
          ],
          id: 'approval-forms-tab-sidebar'
        },
        {
          title: 'Jobs',
          url: '/admin/jobs',
          active: /\/admin\/(seek_)?jobs/,
          id: 'jobs-tab-sidebar'
        },
        {
          title: 'Candidates',
          url: '/admin/candidates',
          active: /\/admin\/candidates/,
          permissions: ['View Candidates'],
          id: 'candidates-tab-sidebar'
        },
        {
          title: 'Events',
          url: '/admin/calendar/events',
          active: /\/admin\/calendar/,
          permissions: ['Create / Edit Events'],
          id: 'events-tab-sidebar'
        },
        {
          title: 'Smart Forms',
          url: '/admin/forms',
          active: /\/admin\/forms/,
          permissions: ['Create / Edit Forms'],
          id: 'smart-forms-tab-sidebar'
        },
        {
          title: 'Full Search',
          url: '/admin/search/full',
          active: /\/admin\/search\/(full|results)/,
          permissions: ['Full Search'],
          id: 'full-search-tab-sidebar'
        }
      ]
    },
    {
      title: 'Admin',
      icon: 'shield',
      id: 'admin-tab-sidebar',
      submenu: [
        ...(leadershipMetricsDashboardEnabled
          ? [
              {
                title: 'Leadership Metrics',
                url: '/admin/leadership_metrics',
                active: /\/admin\/leadership_metrics/,
                permissions: ['View Leadership Metrics'],
                id: 'leadership-metrics-tab-sidebar'
              }
            ]
          : []),
        {
          title: 'Entities',
          url: '/admin/entities',
          active: /\/admin\/entities/,
          permissions: ['Edit Entities'],
          id: 'entities-tab-sidebar'
        },
        ...(hasQuestionLibraryAccess || hasTagManagementAccess || hasJobTemplateAccess
          ? [
              {
                title: 'Templates',
                url: '/admin/templates',
                active: /\/admin\/templates/,
                id: 'templates-tab-sidebar'
              }
            ]
          : []),
        {
          title: 'Reports',
          url: '/admin/reports',
          active: /\/admin\/reports/,
          permissions: ['Applications by Source & Entity Reports', 'Placements Advanced Report'],
          id: 'reports-tab-sidebar'
        },
        {
          title: 'Trash',
          url: '/admin/trash',
          active: /\/admin\/trash/,
          permissions: ['Manage Trashed Jobs / Applications'],
          id: 'trash-tab-sidebar'
        }
      ]
    },
    {
      title: 'Tools',
      icon: 'spanner',
      submenu: [
        {
          title: 'CSV Data',
          url: '/admin/csv-data',
          active: /\/admin\/csv-data/,
          id: 'csv-tab-sidebar'
        },
        {
          title: 'Internal Tools',
          url: '/admin/internal_tools/index',
          active: /\/admin\/internal_tools/,
          id: 'internal-tools-tab-sidebar'
        },
        {
          title: 'System Fields',
          url: '/admin/system_fields',
          active: /\/admin\/system_fields/,
          id: 'system-fields-tab-sidebar'
        },
        {
          title: 'Integrations',
          url: '/admin/integrations',
          active: /\/admin\/integrations/,
          id: 'integrations-tab-sidebar'
        },
        {
          title: 'Menu',
          url: '/admin/integration_menu_items',
          active: /\/admin\/integration_menu_items/,
          id: 'menu-tab-sidebar'
        },
        {
          title: 'Unsubscribes',
          url: '/admin/unsubscribes',
          active: /\/admin\/unsubscribes/,
          id: 'unsubscribes-tab-sidebar'
        }
      ]
    }
  ];
};

export type TMenuItem = ReturnType<typeof defaultMenuItems>[number];
